"use client";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { useRouter } from "next/navigation";
import { useSearchParams, usePathname } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { KeyNames } from "@packages/lib/utlils/filters/filterJson";
import SearchLabelsSkeleton from "@packages/shared-components/skeleton/search-result/search-labels-skeleton";
import dynamic from "next/dynamic";
import { extractUrlAndSessionValues } from "@packages/lib/utlils/filters/result-filters";
import { constructSearchParams } from "@packages/lib/utlils/filters/result-filters";
import { handleCookiesAndSession } from "@packages/lib/utlils/filters/result-filters";
import { GET_FILTER_PRIORITY } from "@packages/shared-components/common-utilities/popups/search-filtercomponent/filter-contant-values/filter-constants";
import { cleanUrl } from "@packages/lib/utlils/url-builder";
import { getCookie } from "@packages/lib/utlils/cookie-utils";
const PlusIconSvg = dynamic(() => import("./plusIcon-svg"));
const RemoveIconSvg = dynamic(() => import("./removeIcon-svg"));
type KeyValueObject = Record<string, string>;

const SearchLabels = ({ searchPayLoad, displayResponse, refererURL }: any) => {
  const router = useRouter();
  const keyName = KeyNames();
  const pathName = usePathname();
  const searchparams = useSearchParams();
  const [labels, setLabels] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isWhatuni = process.env.PROJECT === "Whatuni";
  const staticYearValue = searchparams?.get(keyName?.year)
    ? searchparams?.get(keyName?.year) +
      " - " +
      (Number(searchparams?.get("year")) + 1)
    : new Date().getFullYear() + " - " + (new Date().getFullYear() + 1);

  const year = getCookie("intakeYear");

  const getSearchLabel = () => {
    const filterArray: any[] = [];
    const updateFilterArray = (key: string, displayName: string) => {
      filterArray?.push({
        key,
        displayName,
        textKey: "",
      });
    };
    updateFilterArray(keyName?.year, year || staticYearValue);
    updateFilterArray(
      isWhatuni ? "study-level" : "qualification",
      displayResponse?.studyLevel
    );
    updateFilterArray(
      keyName?.postcode,
      searchPayLoad?.postCode?.toString()?.toUpperCase()
    );
    updateFilterArray(keyName?.studyMode, displayResponse?.studyMode);
    updateFilterArray(keyName?.studyMethod, displayResponse?.studyMethod);
    updateFilterArray(
      keyName?.month,
      searchPayLoad?.intakeMonth?.toString()?.toUpperCase()
    );
    updateFilterArray(keyName?.university, displayResponse?.collegeName);
    const subjectArray = displayResponse?.subjectName;
    if (subjectArray?.length === 1 && typeof subjectArray[0] === "string") {
      filterArray?.push({
        key: keyName?.keyword,
        displayName: subjectArray[0],
        textKey: subjectArray[0]?.toLowerCase(),
      });
    } else {
      subjectArray?.map((subjects: any) => {
        filterArray?.push({
          key: keyName.subject,
          displayName: subjects?.description,
          textKey: subjects?.descriptionTextKey,
        });
      });
    }
    displayResponse?.locationName?.map((location: string) => {
      filterArray.push({
        key: keyName.location,
        displayName: location,
        textKey: location?.toLowerCase(),
      });
    });
    updateFilterArray(keyName?.locationType, displayResponse?.locationType);
    updateFilterArray(keyName?.russellGroup, displayResponse?.universityGroup);
    setLabels(filterArray);
  };

  useEffect(() => {
    getSearchLabel();
    if (refererURL) {
      document.cookie = "filter_param=; path=/; max-age=0";
    }
    setIsLoading(false);
  }, [searchPayLoad]);

  const shouldShowCloseIcon = (index: any) => {
    const isFirstTwo = isWhatuni ? index < 2 : index < 1;
    if (isFirstTwo) return false;
    return true;
  };

  const openFilterFunction = () => {
    emitter.emit("isfilterOpen", "subject");
  };

  const removeFilter = (filterKey: string, newVal: string) => {
    setIsLoading(true);
    if (filterKey === "postcode") {
      emitter.emit("removePostcode");
    }

    const obj = extractUrlAndSessionValues(
      searchparams,
      filterKey,
      `${filterKey === keyName.subject || filterKey == keyName.subject ? newVal : ""}`,
      false,
      true
    );
    const isUniversitySelected = obj[keyName?.university] ? true : false;
    const orderedParams = GET_FILTER_PRIORITY(isUniversitySelected)?.reduce(
      (acc, priorityKey) => {
        if (obj[priorityKey]) acc[priorityKey] = obj[priorityKey];
        return acc;
      },
      {} as KeyValueObject
    );
    const { urlParams, cookieParams } = constructSearchParams(orderedParams);
    const srpageBodySection =
      window?.document?.getElementById("srpageBodySection");
    const prPageBodySection =
      window?.document?.getElementById("prPageBodySection");
    emitter.emit("searchresultloading", true);
    emitter.emit("providerresultloading", true);
    srpageBodySection?.classList?.add("hidden");
    prPageBodySection?.classList?.add("hidden");

    handleCookiesAndSession(cookieParams);

    const pathNameArray = pathName?.split("/")?.filter((item) => item);

    let url = `/${pathNameArray[0]}`;
    const hasSubject = !!orderedParams[keyName?.subject];
    const hasUniversity = !!orderedParams[keyName?.university];
    const queryString = urlParams?.toString();
    const querySuffix = queryString ? `?${queryString}` : "";
    if (isWhatuni) {
      if (
        (filterKey === "university" && hasSubject) ||
        (!hasUniversity && hasSubject)
      ) {
        url += `/search${querySuffix}`;
      } else if (filterKey !== "university" && hasUniversity) {
        url += `/csearch${querySuffix}`;
      }
    } else {
      url = `/pgs/search${querySuffix}`;
    }
    const normalizedUrl = cleanUrl(url);
    window.history.pushState(null, "", normalizedUrl);
    emitter.emit("refreshFilters");
    router.push(normalizedUrl);
  };
  return (
    <>
      {isLoading ? (
        <SearchLabelsSkeleton />
      ) : (
        labels?.length > 0 && (
          <section className="overflow-x-auto snap-x snap-mandatory bg-white px-[16px] py-[8px] md:px-[20px] lg:overflow-hidden xl:px-0 z-[3]">
            <div className="max-w-container mx-auto">
              <ul className="flex items-start gap-[8px] uppercase lg:flex-wrap">
                {labels?.map(
                  (items: any, index: number) =>
                    items?.displayName && (
                      <li
                        className="bg-secondary-50 text-blue-500 whitespace-nowrap rounded-[4px] px-[10px] py-[3px] font-semibold x-small flex items-center gap-[2px]"
                        key={index + 1}
                      >
                        {items?.displayName}
                        {index !== 0 && shouldShowCloseIcon(index) && (
                          <RemoveIconSvg
                            items={items}
                            removeFilter={removeFilter}
                          />
                        )}
                      </li>
                    )
                )}
                <li
                  className="bg-secondary-50 text-blue-500 whitespace-nowrap rounded-[4px] px-[10px] py-[2px] font-semibold x-small flex items-center justify-center gap-[2px] size-[24px] cursor-pointer lg:block"
                  onClick={openFilterFunction}
                >
                  <div aria-label="Plus Icon">
                    <PlusIconSvg />
                  </div>
                </li>
              </ul>
            </div>
          </section>
        )
      )}
    </>
  );
};

export default SearchLabels;
